"use client";

import { createContext, useContext, type ReactNode } from "react";
import type { Job, PipelineStage } from "@/lib/api/types";

interface PipelineJobContextValue {
  job: Job;
  stages: PipelineStage[];
  onRefresh: () => void;
}

const PipelineJobContext = createContext<PipelineJobContextValue | null>(null);

export function PipelineJobProvider({
  job,
  stages,
  onRefresh,
  children,
}: PipelineJobContextValue & { children: ReactNode }) {
  return (
    <PipelineJobContext.Provider value={{ job, stages, onRefresh }}>
      {children}
    </PipelineJobContext.Provider>
  );
}

export function usePipelineJob(): PipelineJobContextValue {
  const ctx = useContext(PipelineJobContext);
  if (!ctx) throw new Error("usePipelineJob must be used within PipelineJobProvider");
  return ctx;
}

/** Non-throwing variant for surfaces that may render outside the provider
 * (e.g. the table header in isolation tests). Returns null when absent. */
export function usePipelineJobOptional(): PipelineJobContextValue | null {
  return useContext(PipelineJobContext);
}
